import axios from 'axios';
import { APP_CONFIG } from '@/shared/config/config';
import { sessionService } from '@/shared/services/session.service';
import { analyzeAuthError, clearAuthData } from './auth-utils';

/**
 * Endpoints "no-críticos" que NO deben gatillar redirect a /login en caso de 401.
 *
 * Bug A fix (2026-06-06): race condition post-login donde sagas (notificationsSaga,
 * etc.) disparan fetches ANTES que el JWT esté disponible en sessionService → 401
 * spurious → clearAuth + redirect → loop infinito.
 *
 * Estos endpoints son de lectura no-crítica. Si fallan con 401 por timing, debe
 * fallar silente (sin clearSession ni redirect). El usuario sigue con su sesión
 * válida; el componente que necesite el dato puede mostrar empty state o retry.
 */
const SILENT_401_ENDPOINTS = [
  '/notifications',
  '/plans/limits',
  '/plans/stats',
  '/subscriptions/trial-days',
  '/ai/consent',
  '/multi-site',
  '/sites',
];

const isSilent401Endpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return SILENT_401_ENDPOINTS.some(ep => url.includes(ep));
};

// Configurar axios globalmente
export const configureAxios = () => {
  // Establecer configuración base para todas las peticiones
  axios.defaults.baseURL = APP_CONFIG.api.baseUrl;
  axios.defaults.withCredentials = true;
  axios.defaults.headers.common['Content-Type'] = 'application/json';

  // Interceptor de request - Bearer token + withCredentials fallback
  axios.interceptors.request.use(
    config => {
      // Keep withCredentials for same-domain cookie fallback (local dev)
      config.withCredentials = true;

      // Send Authorization Bearer header (cross-domain compatible)
      const token = sessionService.getToken();
      if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Adjuntar tenant automáticamente cuando esté disponible
      const user = sessionService.getUser();
      if (user?.tenantId) {
        if (!config.headers) {
          config.headers = {} as any;
        }
        if (!config.headers['x-tenant-id']) {
          config.headers['x-tenant-id'] = user.tenantId;
        }
      }

      // No sobreescribir Content-Type cuando se envía FormData
      if (config.data instanceof FormData && config.headers) {
        delete config.headers['Content-Type'];
      }


      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Interceptor de response - manejar errores de autenticación
  axios.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      // Analizar el error y determinar cómo manejarlo
      const authError = analyzeAuthError(error);

      // Bug A fix: si el 401 viene de un endpoint "no-crítico" (notifications/
      // stats/limits/etc), fallar silente. NO clearSession ni redirect — evita
      // loop de bounce cíclico post-login por race condition con JWT propagation.
      const requestUrl = error.config?.url;
      const isSilentEndpoint =
        authError.status === 401 && isSilent401Endpoint(requestUrl);

      if (isSilentEndpoint) {
        // Solo log debug para auditoría — no afecta sesión ni UX.
        if (typeof console !== 'undefined' && console.debug) {
          console.debug(
            `[axios] 401 silente en endpoint no-crítico (${requestUrl}) — sin redirect`
          );
        }
        return Promise.reject(error);
      }

      // Limpiar datos de autenticación si es necesario
      if (authError.status === 401) {
        clearAuthData();
      }

      // Redirigir si es necesario
      if (authError.shouldRedirect) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }


      return Promise.reject(error);
    }
  );

};

// Configurar axios al importar este módulo
configureAxios();

// Exportar instancia de axios configurada
export const apiClient = axios;

// Default export para nuevos features (trust-badge, multi-site, mortgage, vacation,
// metabroker, featured-listings, property-promotions) que usan
// `import axios from '@/shared/utils/axios-config'`
export default axios;
